/**
 * Group palettes for firmware version ranges.
 * Each group contains shades of the same hue, ordered light → dark.
 */
const GROUP_PALETTES = {
    /** 5.x – 6.x */
    blue: [
        "#a3c4f3",
        "#7baae8",
        "#5b93db",
        "#3d7cce",
        "#2668be",
        "#1a5bb0",
        "#1a4fa0",
        "#153f80",
        "#103060",
        "#0b2040",
    ],
    /** 7.x – 9.x */
    purple: [
        "#d4b5f7",
        "#bf96f0",
        "#a976e8",
        "#9358de",
        "#7d3dd4",
        "#6b2dbf",
        "#5b1d99",
        "#4a1780",
        "#3a1166",
        "#2a0b4d",
    ],
    /** 20.x */
    orange: [
        "#ffd6a5",
        "#ffc078",
        "#ffa94d",
        "#f59030",
        "#e07a1a",
        "#cc6a0a",
        "#b35900",
        "#994d00",
        "#804000",
        "#663300",
    ],
    /** 30.x+ */
    green: [
        "#a8e6cf",
        "#7dd4ac",
        "#5cc48e",
        "#3db370",
        "#2da05e",
        "#1f8c4c",
        "#1b7a3d",
        "#156630",
        "#105024",
        "#0a3a18",
    ],
    /** Everything else (1–4, 10–19, 21–29, non-parseable) */
    teal: [
        "#a6e3e9",
        "#7dd4db",
        "#5ac5cd",
        "#3ab5bf",
        "#22a3ad",
        "#14919b",
        "#0a7f88",
        "#006d75",
        "#005c63",
        "#004a50",
    ],
} as const;

type GroupName = keyof typeof GROUP_PALETTES;

const UNKNOWN_COLOR = "#999999";

/**
 * Parses the leading major version number from a firmware version string.
 *
 * @param {string} firmwareVersion - A version string like "7.2.1" or "30.0".
 * @returns {number | null} The major version number, or null if unparseable.
 */
const parseMajorVersion = (firmwareVersion: string): number | null => {
    const match = firmwareVersion.match(/^(\d+)/);
    if (!match) return null;
    return parseInt(match[1], 10);
};

/**
 * Resolves the palette group name for a given major version number.
 *
 * @param {number} major - The major version number.
 * @returns {GroupName} The group palette key.
 */
const getGroupName = (major: number): GroupName => {
    if (major >= 5 && major <= 6) return "blue";
    if (major >= 7 && major <= 9) return "purple";
    if (major === 20) return "orange";
    if (major >= 30) return "green";
    return "teal";
};

/**
 * Deterministic hash of a string to a number.
 *
 * @param {string} str - The string to hash.
 * @returns {number} A positive integer hash.
 */
const hashString = (str: string): number => {
    let hash = 5381;

    for (let i = 0; i < str.length; i++) {
        hash = (hash * 33) ^ str.charCodeAt(i);
    }

    return hash >>> 0;
};

/**
 * Returns a deterministic color for a firmware version string.
 * Versions are grouped by major version range and assigned shades
 * within that group's hue. The same version always maps to the same
 * color across reloads.
 *
 * Groups:
 * - 5.x – 6.x → Blue shades
 * - 7.x – 9.x → Purple shades
 * - 20.x → Orange/Amber shades
 * - 30.x+ → Green shades
 * - Other → Teal shades
 * - Unknown/null → Gray
 *
 * @param {string | null | undefined} firmwareVersion - The firmware version string.
 * @returns {string} A hex color string.
 */
export const getFirmwareColor = (
    firmwareVersion: string | null | undefined,
): string => {
    if (!firmwareVersion) return UNKNOWN_COLOR;

    const major = parseMajorVersion(firmwareVersion);
    const groupName = major !== null ? getGroupName(major) : "teal";
    const palette = GROUP_PALETTES[groupName];
    const index = hashString(firmwareVersion) % palette.length;

    return palette[index];
};

export { parseMajorVersion, UNKNOWN_COLOR };
